
import { Component, Prop, Vue, Watch } from "vue-property-decorator"
import { CircularLoader, PullRefresh, Require } from "@coredination/components"
import { navbar, NavbarOption } from "@/components/NavbarOptions.vue"

import AssetDetailQuery from "@/graphql/asset/assetdetail.graphql"
import UpdateAsset from "@/graphql/asset/updateasset.graphql"
import EntityEventSubscription from "@/graphql/subscription/entityevent.graphql"
import * as cdn from "@coredination/components"
import { User } from "@coredination/common"
import modaldialog from "@/components/dialogs/modaldialog"

@Component({
    apollo: {
        me: cdn.graphql.me,
        asset: {
            query: AssetDetailQuery,
            skip() {
                return !this.uuid
            },
            variables() {
                return { uuid: this.uuid }
            },
        },
    },
    components: {
        CircularLoader,
        PullRefresh,
        Require,
        StateField: () => import("@/components/fields/StateField.vue"),
        FreeTextField: () => import("@/components/fields/FreeTextField.vue"),
        LabelField: () => import("@/components/fields/LabelField.vue"),
        CustomerField: () => import("@/components/fields/CustomerField.vue"),
        ContactField: () => import("@/components/fields/ContactField.vue"),
        GroupField: () => import("@/components/fields/GroupField.vue"),
        LocationField: () => import("@/components/fields/LocationField.vue"),
        SingleContactField: () => import("@/components/fields/SingleContactField.vue"),
        DocumentsField: () => import("@/components/fields/DocumentsField.vue"),
        CustomFieldsField: () => import("@/components/fields/CustomFieldsField.vue"),
        SelectAssetStateField: () => import("@/components/fields/SelectAssetStateField.vue"),
        UnsavedChangesDialog: () => import("@/components/dialogs/UnsavedChangesDialog.vue"),
    },
    beforeRouteLeave(to, from, next) {
        const dis = this as AssetEdit
        if (modaldialog.preventNavigation()) {
            console.log("modal dialog says no to navigation")
            return next(false)
        }

        // Prevent leaving on unsaved changes
        if (
            dis.changed ||
            ((dis.undoModel.uuid || dis.undoModel.groupUuid) &&
                JSON.stringify(dis.undoModel) != JSON.stringify(dis.model))
        ) {
            dis.showUnsavedChanges = true
            dis.discardChangesNext = next
            return next(false)
        }
        next()
    },
})
export default class AssetEdit extends Vue {
    @Prop()
    uuid?: string

    asset: any = {}
    refreshing = false
    me?: User
    errorRequiredFields: any[] = []

    changed = false
    showUnsavedChanges = false
    discardChangesNext: any = null
    undoModel: any = {}

    get fabOptions() {
        return []
    }

    mounted(): void {
        navbar?.setOptions(this.options)
        this.undoModel = JSON.parse(JSON.stringify(this.model))
    }

    get options(): NavbarOption[] {
        return [
            { icon: "done", action: this.save },
        ]
    }

    @Watch("model")
    watchModel(newModel: any) {
        if (!this.undoModel.uuid || this.undoModel.uuid != newModel.uuid) {
            this.undoModel = JSON.parse(JSON.stringify(newModel))
        }
    }

    @Watch("model", { deep: true })
    deepWatchModel() {
        if (
            (this.undoModel.uuid || this.undoModel.groupUuid) &&
            JSON.stringify(this.undoModel) != JSON.stringify(this.model)
        ) {
            this.changed = true
        }
    }

    addContact() {
        this.asset.jobContact = {
            name: '',
            emailAddress: '',
            phoneNo: '',
            role: ''
        }
    }

    save(){
        const uuid = this.uuid
        const jobContact = this.asset.jobContact
        if (jobContact && jobContact.name == '' && jobContact.emailAddress == '' && jobContact.phoneNo == '') {
            this.asset.jobContact = null
        }
        const input = {
            name: this.asset.name || null,
            assetNo: this.asset.assetNo || null,
            labels: this.asset.labels || null,
            state: this.asset.state || null,
            groupUuid: this.asset.groupUuid || null,
            customerUuid: this.asset.customerUuid || null,
            customerProjectUuid: this.asset.customerProjectUuid || null,
            description: this.asset.description || null,
            customFields: this.asset.customFields ? this.stripTypeNameFromFields(this.asset.customFields) : [],
            location: this.asset.location?.placeName ? this.stripTypeName(this.asset.location) : null,
            locationDescription: this.asset.locationDescription || null,
            jobContact: this.asset.jobContact ? this.stripTypeName(this.asset.jobContact) : null,
        }
        var self = this
        return this.$apollo
            .mutate({
                mutation: UpdateAsset,
                variables: { uuid, input },
                update(proxy, response) {
                    const hackProxy = proxy as any
                    Object.keys(hackProxy.data.data).forEach(key => {
                        if (key.match(/(job|myJobs|myWorkStats|JobListItem)/)) hackProxy.data.delete(key)
                        if (key.match(/(asset|updateAsset)/)) hackProxy.data.delete(key)
                    })
                }
            })
            .then(async response => {
                console.log("asset saved", response)
                Object.assign(this.asset, response.data.updateAsset)
                this.changed = false
                this.undoModel = {}
                self.$router.back()
            })
            .catch(error => { console.log(error) })
    }

    stripTypeName(entity: any) {
        if (entity.__typename) delete entity.__typename
        return entity
    }

    stripTypeNameFromFields(dirtyFields: any[]) {
        if (!dirtyFields || dirtyFields.length == 0) return null
        return dirtyFields.map((field: any) => {
            const { ["__typename"]: _, ...cleanFields } = field
            return cleanFields
        })
    }

    clearErrorRequired(field: string) {
        this.errorRequiredFields = this.errorRequiredFields.filter((e) => e !== field)
    }

    get errorRequired(): any {
        return this.errorRequiredFields
            ? this.errorRequiredFields.reduce((obj: any, key: string) => ((obj[key] = true), obj), {})
            : null
    }

    hasMultipleActiveGroups() {
        return this.me && this.me.groupMemberships && this.me.groupMemberships.filter((m: any) => m.active).length > 1
    }

    get loading() {
        return this.refreshing || (this.$apollo.loading && !this.asset.name)
    }

    get model() {
        return this.asset
    }
    set model(updated: any) {
        this.asset = updated
    }

    async refresh() {
        this.refreshing = true
        await this.$apollo.queries.asset.refetch()
        this.refreshing = false
    }

    iosWrapperClient() {
        return Vue.prototype.$iosWrapperClient
    }

    discardChanges() {
        this.changed = false
        this.discardChangesNext()
        setTimeout(() => {
            Object.assign(this.model, this.undoModel)
            this.undoModel = {}
        }, 100)
    }
}
